import { type FormEvent, useEffect, useState } from "react";

import { api, rows } from "../lib/api";
import type { ApiList } from "../lib/types";

export interface ProjectPosition {
  id: number;
  code: string;
  nom: string;
  capacite: number;
}

export interface ProjectAssignment {
  id: number;
  poste: number;
  intervenant: number;
  allocation_pct: string;
  statut: string;
}

interface Person {
  id: number;
  nom_complet: string;
  actif: boolean;
}

interface Props {
  projectId: number;
  positions: ProjectPosition[];
  assignments: ProjectAssignment[];
  onRefresh: () => Promise<void>;
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Opération impossible.";
}

export function ProjectTeam({ projectId, positions, assignments, onRefresh }: Props) {
  const [people, setPeople] = useState<Person[]>([]);
  const [position, setPosition] = useState({ code: "", nom: "", capacite: 1 });
  const [person, setPerson] = useState({ nom_complet: "", email: "" });
  const [assignment, setAssignment] = useState({ poste: "", intervenant: "", allocation_pct: "100" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadPeople() {
    try {
      setPeople(rows(await api.get<ApiList<Person>>("/api/v1/projects/intervenants/")));
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  useEffect(() => { void loadPeople(); }, [projectId]);

  async function run(action: () => Promise<void>) {
    setError("");
    setBusy(true);
    try {
      await action();
      await onRefresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  function addPosition(event: FormEvent) {
    event.preventDefault();
    void run(async () => {
      await api.post("/api/v1/projects/postes/", {
        projet: projectId,
        code: position.code.trim().toUpperCase(),
        nom: position.nom.trim(),
        capacite: position.capacite
      });
      setPosition({ code: "", nom: "", capacite: 1 });
    });
  }

  function addPerson(event: FormEvent) {
    event.preventDefault();
    void run(async () => {
      const created = await api.post<Person>("/api/v1/projects/intervenants/", {
        nom_complet: person.nom_complet.trim(),
        email: person.email.trim()
      });
      setPerson({ nom_complet: "", email: "" });
      setAssignment((current) => ({ ...current, intervenant: String(created.id) }));
      await loadPeople();
    });
  }

  function addAssignment(event: FormEvent) {
    event.preventDefault();
    void run(async () => {
      await api.post("/api/v1/projects/affectations/", {
        projet: projectId,
        poste: Number(assignment.poste),
        intervenant: Number(assignment.intervenant),
        allocation_pct: assignment.allocation_pct,
        statut: "ACTIVE"
      });
      setAssignment({ poste: "", intervenant: "", allocation_pct: "100" });
    });
  }

  return (
    <section className="panel mt-6 p-5">
      <h2 className="text-lg font-bold">Équipe du projet</h2>
      <p className="mt-1 text-sm text-slate-500">Une personne peut travailler sur plusieurs projets de la même entreprise. Chaque affectation est historisée.</p>
      {error && <p role="alert" className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">{error}</p>}

      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <form onSubmit={addPosition} className="space-y-3 rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold">Créer un poste</h3>
          <input className="field" aria-label="Code du poste" placeholder="Code du poste" value={position.code} onChange={(event) => setPosition({ ...position, code: event.target.value })} required />
          <input className="field" aria-label="Nom du poste" placeholder="Nom du poste" value={position.nom} onChange={(event) => setPosition({ ...position, nom: event.target.value })} required />
          <label className="block text-sm">Capacité<input className="field mt-1" type="number" min="1" value={position.capacite} onChange={(event) => setPosition({ ...position, capacite: Number(event.target.value) })} required /></label>
          <button disabled={busy} className="btn-secondary">Créer le poste</button>
        </form>

        <form onSubmit={addPerson} className="space-y-3 rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold">Ajouter une personne</h3>
          <input className="field" aria-label="Nom complet" placeholder="Nom complet" value={person.nom_complet} onChange={(event) => setPerson({ ...person, nom_complet: event.target.value })} required />
          <input className="field" type="email" aria-label="E-mail facultatif" placeholder="E-mail facultatif" value={person.email} onChange={(event) => setPerson({ ...person, email: event.target.value })} />
          <button disabled={busy} className="btn-secondary">Ajouter la personne</button>
        </form>

        <form onSubmit={addAssignment} className="space-y-3 rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold">Affecter au projet</h3>
          <select className="field" aria-label="Poste" value={assignment.poste} onChange={(event) => setAssignment({ ...assignment, poste: event.target.value })} required><option value="">Choisir un poste</option>{positions.map((item) => <option key={item.id} value={item.id}>{item.code} · {item.nom}</option>)}</select>
          <select className="field" aria-label="Personne" value={assignment.intervenant} onChange={(event) => setAssignment({ ...assignment, intervenant: event.target.value })} required><option value="">Choisir une personne</option>{people.filter((item) => item.actif).map((item) => <option key={item.id} value={item.id}>{item.nom_complet}</option>)}</select>
          <label className="block text-sm">Temps alloué (%)<input className="field mt-1" type="number" min="0.01" max="100" step="0.01" value={assignment.allocation_pct} onChange={(event) => setAssignment({ ...assignment, allocation_pct: event.target.value })} required /></label>
          <button disabled={busy || !positions.length || !people.length} className="btn-primary">Affecter</button>
        </form>
      </div>

      <div className="mt-5 space-y-2">
        {assignments.map((item) => <AssignmentRow key={item.id} assignment={item} position={positions.find((entry) => entry.id === item.poste)} person={people.find((entry) => entry.id === item.intervenant)} disabled={busy} onSave={(patch) => run(async () => { await api.patch(`/api/v1/projects/affectations/${item.id}/`, patch); })} />)}
        {!assignments.length && <p className="text-sm text-slate-500">Aucune affectation pour ce projet.</p>}
      </div>
    </section>
  );
}

function AssignmentRow({ assignment, position, person, disabled, onSave }: {
  assignment: ProjectAssignment;
  position?: ProjectPosition;
  person?: Person;
  disabled: boolean;
  onSave: (patch: { allocation_pct: string; statut: string }) => Promise<void>;
}) {
  const [allocation, setAllocation] = useState(assignment.allocation_pct);
  const [status, setStatus] = useState(assignment.statut);
  useEffect(() => { setAllocation(assignment.allocation_pct); setStatus(assignment.statut); }, [assignment.allocation_pct, assignment.statut]);
  return <div className="flex flex-wrap items-end gap-3 rounded-xl bg-slate-50 p-3 text-sm">
    <div className="min-w-40 flex-1"><strong>{person?.nom_complet || `Personne #${assignment.intervenant}`}</strong><div className="text-slate-500">{position?.nom || `Poste #${assignment.poste}`}</div></div>
    <label>Allocation (%)<input className="field mt-1 w-28" type="number" min="0.01" max="100" step="0.01" value={allocation} onChange={(event) => setAllocation(event.target.value)} /></label>
    <label>État<select className="field mt-1" value={status} onChange={(event) => setStatus(event.target.value)}><option value="PLANNED">Prévue</option><option value="ACTIVE">Active</option><option value="ENDED">Terminée</option><option value="CANCELLED">Annulée</option></select></label>
    <button className="btn-secondary" disabled={disabled || !allocation || Number(allocation) <= 0 || Number(allocation) > 100} onClick={() => void onSave({ allocation_pct: allocation, statut: status })}>Enregistrer</button>
  </div>;
}
